import React, { useState, useEffect } from 'react';
import {
    CreditCard, Check, Star, Users, Music, Upload,
    Headphones, Shield, Crown, ArrowRight,
    X, User, Mail, Lock, Eye, EyeOff, CheckCircle, Calendar, Zap
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SubscriptionCheckout from '../components/SubscriptionCheckout';

const Subscriptions = () => {
    const { user, register, hasActiveSubscription, subscriptionPlan } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const [showSignupModal, setShowSignupModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    
    // Signup form state
    const [signupData, setSignupData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        username: '',
        displayName: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [signupErrors, setSignupErrors] = useState({});

    useEffect(() => {
        fetchSubscriptionPlans();
    }, []);

    // After plans load: if user is authenticated and a plan was saved before a reload, restore it
    useEffect(() => {
        if (!loading && user) {
            const saved = sessionStorage.getItem('deepwave_pending_plan');
            if (saved) {
                try {
                    const plan = JSON.parse(saved);
                    sessionStorage.removeItem('deepwave_pending_plan');
                    setSelectedPlan(plan);
                    setShowCheckoutModal(true);
                } catch {
                    sessionStorage.removeItem('deepwave_pending_plan');
                }
            }
        }
    }, [loading, user]);

    const fetchSubscriptionPlans = async () => {
        try {
            setLoading(true);
            const environment = import.meta.env.VITE_SQUARE_ENVIRONMENT || 'sandbox';
            const response = await api.get(`/payments/plans/public?environment=${environment}`);
            
            if (response.data.success) {
                // Public endpoint already returns only active plans
                setPlans(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching subscription plans:', error);
            // Set default plans as fallback
            setPlans([
                {
                    id: 'basic_monthly',
                    name: 'Basic Membership',
                    description: 'Access to basic features and unlimited uploads',
                    price: 999,
                    billing_interval: 'MONTHLY',
                    currency: 'USD',
                    is_active: true
                },
                {
                    id: 'premium_monthly', 
                    name: 'Premium Membership',
                    description: 'Full access to all features, priority support, and exclusive content',
                    price: 1999,
                    billing_interval: 'MONTHLY',
                    currency: 'USD',
                    is_active: true
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount / 100);
    };

    const handleSelectPlan = (plan) => {
        setSelectedPlan(plan);

        if (!user) {
            // Persist plan so it survives any page reload during signup/login
            sessionStorage.setItem('deepwave_pending_plan', JSON.stringify(plan));
            setShowSignupModal(true);
        } else {
            setShowCheckoutModal(true);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setSignupErrors({});

        // Validate form
        const errors = {};
        if (!signupData.email) errors.email = 'Email is required';
        if (!signupData.password) errors.password = 'Password is required';
        if (signupData.password !== signupData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }
        if (!signupData.username) errors.username = 'Username is required';
        if (!signupData.displayName) errors.displayName = 'Display name is required';

        if (Object.keys(errors).length > 0) {
            setSignupErrors(errors);
            return;
        }

        try {
            setIsProcessing(true);

            const result = await register({
                email: signupData.email,
                password: signupData.password,
                username: signupData.username,
                displayName: signupData.displayName
            });

            if (result.success) {
                sessionStorage.removeItem('deepwave_pending_plan');
                setShowSignupModal(false);
                setShowCheckoutModal(true);
            } else {
                setSignupErrors({ general: result.error || 'Failed to create account. Please try again.' });
            }
        } catch (error) {
            console.error('Signup error:', error);
            setSignupErrors({ general: 'Failed to create account. Please try again.' });
        } finally {
            setIsProcessing(false);
        }
    };

    const handlePaymentSuccess = (paymentResult) => {
        setPaymentSuccess(true);
        setShowCheckoutModal(false);
        
        // Show success message for a few seconds then redirect
        setTimeout(() => {
            navigate('/dashboard');
        }, 3000);
    };

    const handlePaymentError = (error) => {
        console.error('Payment error:', error);
        // Error is handled within the checkout component
    };

    const getPlanFeatures = (plan) => {
        let features = plan.features;
        if (typeof features === 'string') {
            try { features = JSON.parse(features); } catch { features = null; }
        }
        if (Array.isArray(features) && features.length > 0) return features;
        return [];
    };

    const getPlanIcon = (planName) => {
        return planName.toLowerCase().includes('premium') ? Crown : Music;
    };

    const getPlanColor = (planName) => {
        return planName.toLowerCase().includes('premium') ? 'purple' : 'blue';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-3 animate-pulse" />
                        <p className="text-gray-400">Loading subscription plans...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-5xl font-black gradient-text mb-4">
                        Support The Deep Wave
                    </h1>
                    <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                        Unlock the full potential of our website by subscribing and supporting the art. From new and premieum websiite content to stickers, merchandise and more. 
                        Pick a plan for your music journey with us and please know that your support in any way is much appreciated.
                    </p>
                </div>

                {/* Current Plan Banner */}
                {user && (
                    <div className={`glass-card p-6 mb-10 ${hasActiveSubscription ? 'border border-green-500/30' : 'border border-gray-700/50'}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${hasActiveSubscription ? 'bg-green-600/20' : 'bg-gray-700/50'}`}>
                                    {hasActiveSubscription ? <CheckCircle className="w-6 h-6 text-green-400" /> : <CreditCard className="w-6 h-6 text-gray-400" />}
                                </div>
                                <div>
                                    <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-0.5">Current Plan</p>
                                    {hasActiveSubscription && subscriptionPlan ? (
                                        <>
                                            <p className="text-lg font-bold text-white">{subscriptionPlan.plan_name || subscriptionPlan.name || 'Active Membership'}</p>
                                            <div className="flex flex-wrap items-center gap-3 mt-1">
                                                <span className="inline-flex items-center gap-1 text-xs text-green-400 font-medium">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block"></span>
                                                    Active
                                                </span>
                                                {subscriptionPlan.charged_through_date && (
                                                    <span className="flex items-center gap-1 text-xs text-gray-400">
                                                        <Calendar className="w-3 h-3" />
                                                        Renews {new Date(subscriptionPlan.charged_through_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </span>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-lg font-bold text-white">Free</p>
                                            <p className="text-xs text-gray-400 mt-0.5">Upgrade to unlock exclusive content and features</p>
                                        </>
                                    )}
                                </div>
                            </div>
                            {!hasActiveSubscription && (
                                <div className="flex items-center gap-2 text-blue-400 text-sm font-medium sm:flex-shrink-0">
                                    <Zap className="w-4 h-4" />
                                    Choose a plan below to get started
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Subscription Plans */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                    {plans.map((plan) => {
                        const isYearly = plan.billing_interval === 'YEARLY';
                        const badgeLabel = plan.badge_text || null;
                        const isCurrentPlan = hasActiveSubscription && subscriptionPlan && (
                            subscriptionPlan.plan_id === plan.id ||
                            (subscriptionPlan.plan_name || '').toLowerCase() === plan.name.toLowerCase()
                        );

                        return (
                            <div
                                key={plan.id}
                                className={`glass-card p-8 relative overflow-hidden transition-all duration-300 hover:scale-105 ${
                                    isCurrentPlan
                                        ? 'border border-green-500/50 bg-gradient-to-br from-green-900/10 to-gray-900'
                                        : isYearly
                                            ? 'border border-purple-500/50 bg-gradient-to-br from-purple-900/20 to-gray-900'
                                            : 'border border-blue-500/30'
                                }`}
                            >
                                {/* Current plan badge — takes priority over badge_text */}
                                {isCurrentPlan ? (
                                    <div className="absolute top-4 right-4">
                                        <span className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                                            <CheckCircle className="w-3 h-3" />
                                            Current Plan
                                        </span>
                                    </div>
                                ) : badgeLabel && (
                                    <div className="absolute top-4 right-4">
                                        <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                                            <Star className="w-3 h-3" />
                                            {badgeLabel}
                                        </span>
                                    </div>
                                )}

                                {/* Plan header */}
                                <div className={`text-center mb-6 ${(isCurrentPlan || badgeLabel) ? 'mt-6' : ''}`}>
                                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                                        isYearly ? 'bg-purple-600' : 'bg-blue-600'
                                    }`}>
                                        {isYearly ? <Crown className="w-8 h-8 text-white" /> : <Music className="w-8 h-8 text-white" />}
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                                    <p className="text-gray-400 text-sm">{plan.description}</p>
                                </div>

                                {/* Pricing */}
                                <div className="text-center mb-6">
                                    <div className="flex items-baseline justify-center gap-1">
                                        <span className="text-4xl font-bold text-white">
                                            {formatCurrency(plan.price, plan.currency)}
                                        </span>
                                        <span className="text-gray-400">
                                            /{plan.billing_interval === 'YEARLY' ? 'year' : 'mo'}
                                        </span>
                                    </div>
                                </div>

                                {/* Features */}
                                <div className="space-y-3 mb-8">
                                    {getPlanFeatures(plan).map((feature, index) => (
                                        <div key={index} className="flex items-center gap-3">
                                            <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                                            <span className="text-gray-300 text-sm">{feature}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA Button */}
                                {isCurrentPlan ? (
                                    <div className="w-full py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 bg-green-600/20 text-green-400 border border-green-500/30 cursor-default">
                                        <CheckCircle className="w-4 h-4" />
                                        You're on this plan
                                    </div>
                                ) : hasActiveSubscription ? (
                                    <div className="relative group">
                                        <button
                                            disabled
                                            className="w-full py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 bg-gray-700/50 text-gray-500 border border-gray-600/30 cursor-not-allowed"
                                        >
                                            Contact support to switch plans
                                        </button>
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-xs text-gray-300 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                            Plan switching coming soon
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => handleSelectPlan(plan)}
                                        className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                                            isYearly
                                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                                        }`}
                                    >
                                        Get Started
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Features comparison */}
                {(() => {
                    if (plans.length === 0) return null;

                    const sorted = [...plans].sort((a, b) =>
                        (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.price - b.price
                    );

                    const effective = sorted.map(plan => new Set(getPlanFeatures(plan)));
                    const allFeatures = [...new Set(sorted.flatMap(p => getPlanFeatures(p)))];

                    if (allFeatures.length === 0) return null;

                    return (
                        <div className="glass-card p-5 sm:p-8">
                            <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">Compare Features</h2>

                            {/* Mobile: stacked plan cards */}
                            <div className="md:hidden space-y-4">
                                {sorted.map((plan, idx) => (
                                    <div
                                        key={plan.id}
                                        className={`rounded-lg border p-4 ${
                                            plan.billing_interval === 'YEARLY'
                                                ? 'border-purple-500/40 bg-purple-900/10'
                                                : 'border-blue-500/20 bg-gray-800/40'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <p className={`font-semibold text-sm ${plan.billing_interval === 'YEARLY' ? 'text-purple-300' : 'text-blue-300'}`}>
                                                    {plan.name}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {formatCurrency(plan.price, plan.currency)}/{plan.billing_interval === 'YEARLY' ? 'yr' : 'mo'}
                                                </p>
                                            </div>
                                            {effective[idx].size > 0 && (
                                                <span className="text-xs text-gray-500">{effective[idx].size} features</span>
                                            )}
                                        </div>
                                        {effective[idx].size === 0 ? (
                                            <p className="text-xs text-gray-600 italic">No features listed</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {allFeatures.filter(f => effective[idx].has(f)).map((feature, i) => (
                                                    <div key={i} className="flex items-center gap-2">
                                                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                                                        <span className="text-gray-300 text-sm">{feature}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Desktop: full comparison table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-700">
                                            <th className="text-left py-3 px-4 text-gray-300 w-1/3">Feature</th>
                                            {sorted.map(plan => (
                                                <th key={plan.id} className="text-center py-3 px-4 min-w-[130px]">
                                                    <div className={`font-semibold text-sm ${plan.billing_interval === 'YEARLY' ? 'text-purple-300' : 'text-blue-300'}`}>
                                                        {plan.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500 font-normal mt-0.5">
                                                        {formatCurrency(plan.price, plan.currency)}/{plan.billing_interval === 'YEARLY' ? 'yr' : 'mo'}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-700">
                                        {allFeatures.map((feature, i) => (
                                            <tr key={i} className="hover:bg-white/5 transition-colors">
                                                <td className="py-3 px-4 text-gray-300 text-sm">{feature}</td>
                                                {effective.map((featureSet, idx) => (
                                                    <td key={sorted[idx].id} className="text-center py-3 px-4">
                                                        {featureSet.has(feature)
                                                            ? <Check className="w-5 h-5 text-green-400 mx-auto" />
                                                            : <span className="text-gray-600">—</span>
                                                        }
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })()}

                {/* FAQ Section */}
                <div className="mt-12 glass-card p-8">
                    <h2 className="text-2xl font-bold text-white mb-6 text-center">Frequently Asked Questions</h2>
                    
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-2">Can I change my plan later?</h3>
                            <p className="text-gray-400">Yes, you can upgrade or downgrade your plan at any time from your account settings.</p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-2">What payment methods do you accept?</h3>
                            <p className="text-gray-400">We accept all major credit cards through our secure Square payment system.</p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-2">Can I cancel anytime?</h3>
                            <p className="text-gray-400">Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Signup Modal */}
            {showSignupModal && selectedPlan && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
                    <div className="glass-card max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-white">Create Account</h3>
                            <button
                                onClick={() => {
                                    sessionStorage.removeItem('deepwave_pending_plan');
                                    setShowSignupModal(false);
                                    setSelectedPlan(null);
                                }}
                                className="text-gray-400 hover:text-white"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="mb-4 p-3 bg-blue-600/20 rounded-lg">
                            <p className="text-blue-400 text-sm">
                                Sign up to continue with <strong>{selectedPlan.name}</strong> - {formatCurrency(selectedPlan.price)}/{selectedPlan.billing_interval === 'YEARLY' ? 'year' : 'mo'}
                            </p>
                        </div>

                        {signupErrors.general && (
                            <div className="mb-4 p-3 bg-red-600/20 rounded-lg">
                                <p className="text-red-400 text-sm">{signupErrors.general}</p>
                            </div>
                        )}

                        <form onSubmit={handleSignup} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="email"
                                        value={signupData.email}
                                        onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                                        className={`w-full pl-10 pr-3 py-2 bg-gray-800 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                                            signupErrors.email ? 'border-red-500' : 'border-gray-700'
                                        }`}
                                        placeholder="Enter your email"
                                        required
                                    />
                                </div>
                                {signupErrors.email && <p className="text-red-400 text-sm mt-1">{signupErrors.email}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        value={signupData.username}
                                        onChange={(e) => setSignupData({...signupData, username: e.target.value})}
                                        className={`w-full pl-10 pr-3 py-2 bg-gray-800 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                                            signupErrors.username ? 'border-red-500' : 'border-gray-700'
                                        }`}
                                        placeholder="Choose a username"
                                        required
                                    />
                                </div>
                                {signupErrors.username && <p className="text-red-400 text-sm mt-1">{signupErrors.username}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Display Name</label>
                                <input
                                    type="text"
                                    value={signupData.displayName}
                                    onChange={(e) => setSignupData({...signupData, displayName: e.target.value})}
                                    className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                                        signupErrors.displayName ? 'border-red-500' : 'border-gray-700'
                                    }`}
                                    placeholder="Your display name"
                                    required
                                />
                                {signupErrors.displayName && <p className="text-red-400 text-sm mt-1">{signupErrors.displayName}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={signupData.password}
                                        onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                                        className={`w-full pl-10 pr-10 py-2 bg-gray-800 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                                            signupErrors.password ? 'border-red-500' : 'border-gray-700'
                                        }`}
                                        placeholder="Create a password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                {signupErrors.password && <p className="text-red-400 text-sm mt-1">{signupErrors.password}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="password"
                                        value={signupData.confirmPassword}
                                        onChange={(e) => setSignupData({...signupData, confirmPassword: e.target.value})}
                                        className={`w-full pl-10 pr-3 py-2 bg-gray-800 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                                            signupErrors.confirmPassword ? 'border-red-500' : 'border-gray-700'
                                        }`}
                                        placeholder="Confirm your password"
                                        required
                                    />
                                </div>
                                {signupErrors.confirmPassword && <p className="text-red-400 text-sm mt-1">{signupErrors.confirmPassword}</p>}
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-700">
                                <button
                                    type="button"
                                    onClick={() => {
                                        sessionStorage.removeItem('deepwave_pending_plan');
                                        setShowSignupModal(false);
                                        setSelectedPlan(null);
                                    }}
                                    className="btn-secondary"
                                    disabled={isProcessing}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn-primary"
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? 'Creating Account...' : 'Create Account & Continue'}
                                </button>
                            </div>
                        </form>

                        <div className="mt-4 text-center">
                            <p className="text-gray-400 text-sm">
                                Already have an account?{' '}
                                <button 
                                    onClick={() => {
                                        setShowSignupModal(false);
                                        navigate('/login', { state: { returnTo: '/subscriptions' } });
                                    }}
                                    className="text-blue-400 hover:text-blue-300"
                                >
                                    Sign in
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Checkout Modal */}
            {showCheckoutModal && selectedPlan && user && (
                <SubscriptionCheckout
                    plan={selectedPlan}
                    isOpen={showCheckoutModal}
                    onClose={() => {
                        setShowCheckoutModal(false);
                        setSelectedPlan(null);
                    }}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                />
            )}

            {/* Payment Success Modal */}
            {paymentSuccess && (
                <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
                    <div className="glass-card max-w-md w-full p-8 text-center">
                        <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Payment Successful!</h3>
                        <p className="text-gray-400 mb-4">
                            Welcome to {selectedPlan?.name}! Your subscription is now active.
                        </p>
                        <p className="text-sm text-gray-500">
                            Redirecting to your dashboard...
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Subscriptions;